import Card from '@material-ui/core/Card';
import Paper from '@material-ui/core/Paper';
import React, { FunctionComponent, useContext } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import useStyles from './registration.styles';
import RegistrationFormProps from './registration.types';
import AccountInformation from './AccountInformation';
import AccountVerification from './AccountVerification';
import { RegistrationPageStep } from './registration.rules';
import useWrapWithLoader from '../../hooks/useWrapWithLoading';
import { useIsEmailExists } from '../../hooks/useIsEmailExists';
import Page2ColumnLayout from '../utility/page2-column-layout';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import routes from '../../routes';
import { navigate } from '@reach/router';
import RegistrationFormFooter from './RegistrationFormFooter';
import usePgeQuery from '../../hooks/usePgeQuery';
import {
  CommercialAccountValidation,
  GetPersonTypeListResponse,
  OnlineRegistrationType,
  PersonPrimaryIdType,
  QueryGetPersonTypeListArgs,
  QueryValidateCommercialAccountNumberArgs,
  RegistrationPersonType,
} from '../../__generated__/pge-types';
import {
  getPersonTypeListQuery,
  validateCommercialAccount,
} from '../../queries/registration.query';
import { NotificationsContext } from '../../providers/NotificationsProvider';
import { displayPhoneNumberFormat } from '../../util/format';
import TextLink from '../text-link';
import theme from '../../themes/theme';
import { Link } from 'gatsby';

const allowedIDTypes = [
  PersonPrimaryIdType.Dl,
  PersonPrimaryIdType.Ssn,
  PersonPrimaryIdType.Ortrib,
  PersonPrimaryIdType.Visa,
  PersonPrimaryIdType.Matr,
  PersonPrimaryIdType.Military,
  PersonPrimaryIdType.Resalien,
  PersonPrimaryIdType.Itin,
  PersonPrimaryIdType.Pssprt,
  PersonPrimaryIdType.Duns,
  PersonPrimaryIdType.Wrksch,
  PersonPrimaryIdType.Essid,
  PersonPrimaryIdType.Ein,
];

const Registration: FunctionComponent<RegistrationFormProps> = props => {
  const classes = useStyles(props);
  const { t, richT } = useTranslation();
  const { wrapWithLoader } = useWrapWithLoader();
  const { form } = props;
  const { step } = form.values;
  const { isEmailExists } = useIsEmailExists();

  const { refetch: getPersonTypes } = usePgeQuery<
    { getPersonTypeList?: GetPersonTypeListResponse | null },
    QueryGetPersonTypeListArgs
  >(getPersonTypeListQuery, {
    skip: true,
  });

  const { refetch: validateCommercialAccountQuery } = usePgeQuery<
    { validateCommercialAccountNumber: CommercialAccountValidation },
    QueryValidateCommercialAccountNumberArgs
  >(validateCommercialAccount, {
    skip: true,
  });

  const notificationContext = useContext(NotificationsContext);

  const handleError = (errorMessage: any) => {
    notificationContext.setState({
      isOpen: true,
      message: errorMessage,
      severity: 'error',
    });
    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const onSubmitAccountInformation = wrapWithLoader(async () => {
    //Check if email id already registered
    try {
      //pass the phone number or account number to find the person ID details
      const payload: QueryGetPersonTypeListArgs = {
        ...(form.values.primaryAccountOption === 'phoneNumber'
          ? {
              phoneNumber: displayPhoneNumberFormat(form.values.phoneNumber),
              accountNumber: '',
            }
          : { accountNumber: form.values.accountNumber, phoneNumber: '' }),
      };

      const promises: Promise<any>[] = [
        isEmailExists(form.values.email),
        getPersonTypes(payload),
      ];

      if (form.values.primaryAccountOption === 'accountNumber') {
        promises.push(
          validateCommercialAccountQuery({
            accountNumber: form.values.accountNumber,
          }),
        );
      }

      const [
        isExisting,
        getPersonTypeResult,
        validateCommercialAccountResponse,
      ] = await Promise.all(promises);

      if (isExisting) {
        form.setError(
          'email',
          <span>
            {t('EMAIL_ALREADY_REGISTERED_1')}{' '}
            <Link to={routes.SIGN_IN}>{t('SIGN_IN')}</Link>{' '}
            {t('EMAIL_ALREADY_REGISTERED_2')}
          </span>,
        );
        return false;
      }

      const getPersonTypeList = getPersonTypeResult?.data?.getPersonTypeList as
        | GetPersonTypeListResponse
        | null
        | undefined;

      if (!getPersonTypeList) {
        handleError(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'));
        return;
      }

      //Check if account/phone not found in the system
      if (getPersonTypeList.error) {
        handleError(t('REGISTRATION_ACCOUNT_DETAILS_NOT_FOUND'));
        return;
      }

      //Allow only valid ID types for an account.
      const personTypes = getPersonTypeList.personTypes?.filter(
        (
          pType,
        ): pType is RegistrationPersonType & { type: PersonPrimaryIdType } =>
          Boolean(pType?.type && allowedIDTypes.includes(pType.type)),
      );

      if (personTypes && personTypes.length > 0) {
        const list = [
          ...new Set(personTypes?.map(p => p.type)),
          PersonPrimaryIdType.None,
        ];
        void form.setValue('availableExistingIDTypes', list || []);
        void form.setValue('existingIDType', list[0]);
      } else {
        void form.setValue('availableExistingIDTypes', []);
      }

      if (
        validateCommercialAccountResponse &&
        form.values.primaryAccountOption === 'accountNumber'
      ) {
        const isBusiness =
          (validateCommercialAccountResponse?.data
            ?.validateCommercialAccountNumber as CommercialAccountValidation)
            .isValidBusinessAccount || false;
        //Choose the business form for the verification step
        if (isBusiness) {
          void form.setValue(
            'registrationType',
            OnlineRegistrationType.Commercial,
          );
        } else {
          void form.setValue(
            'registrationType',
            OnlineRegistrationType.Residential,
          );
        }
      }

      void form.setValue('step', RegistrationPageStep.VERIFY);
      typeof window !== 'undefined' && window.scrollTo(0, 0);
    } catch (e) {
      handleError(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'));
    }
  });

  return (
    <Page2ColumnLayout>
      <Box>
        <Typography
          className={classes.textPrimary}
          variant={'h1'}
          color={'textPrimary'}
          gutterBottom
        >
          {t('REGISTRATION_HEADER')}
        </Typography>
        <Typography
          className={classes.textPrimary}
          color={'textPrimary'}
          variant={'h4'}
        >
          {richT('REGISTER_TO_VIEW_YOUR_ENERGY_USE')}
        </Typography>

        <Typography
          className={classes.textPrimary}
          variant={'h2'}
          color={'textPrimary'}
          gutterBottom
        >
          {t('WHO_CAN_REGISTER')}
        </Typography>
        <Typography
          className={classes.textPrimary}
          color={'textPrimary'}
          variant={'h4'}
        >
          {richT('WHO_CAN_REGISTER_DESC')}
        </Typography>

        <Box className={classes.container}>
          <form className={classes.root} noValidate>
            <Paper
              elevation={0}
              component={'main'}
              style={{ justifyContent: 'flex-start' }}
            >
              <Card className={classes.card}>
                {step === RegistrationPageStep.INFO && (
                  <AccountInformation classes={classes} {...props} />
                )}

                {step === RegistrationPageStep.VERIFY && (
                  <AccountVerification classes={classes} {...props} />
                )}

                <Box css={{ marginTop: '8px' }}>
                  <Typography
                    className={classes.textPrimary}
                    color={'textPrimary'}
                    variant={'subtitle1'}
                  >
                    {t('CALL_FOR_ASSISTANCE') + ' '}{' '}
                    <TextLink
                      external
                      textColor={theme.palette.primary.main}
                      to={`tel:${t('CALL_FOR_ASSISTANCE_NUMBER_REGISTRATION')}`}
                    >
                      {t('CALL_FOR_ASSISTANCE_NUMBER_REGISTRATION')}
                    </TextLink>
                  </Typography>
                  <Typography
                    component={'span'}
                    color={'textPrimary'}
                    variant={'subtitle1'}
                    style={{ marginTop: 8 }}
                  >
                    {t('PRIVACY_AGREEMENT')}
                    <TextLink
                      textColor={theme.palette.primary.main}
                      to={routes.PRIVACY_POLICY}
                    >
                      {` ${t('PRIVACY_POLICY')}`}
                    </TextLink>
                    {'.'}
                  </Typography>
                </Box>
              </Card>
            </Paper>
          </form>
        </Box>
      </Box>
      {step === RegistrationPageStep.INFO && (
        <RegistrationFormFooter
          nextText={t('NEXT')}
          onNext={form.submit(onSubmitAccountInformation)}
          backText={t('CANCEL')}
          theme="Emphasis"
          onBack={() => {
            void navigate(routes.HOME);
          }}
        />
      )}
      {step === RegistrationPageStep.VERIFY && (
        <RegistrationFormFooter
          nextText={t('REGISTER')}
          onNext={() => form.handleSubmit()}
          backText={t('BACK')}
          theme="Emphasis"
          onBack={() => {
            void form.setValue('step', RegistrationPageStep.INFO);
          }}
        />
      )}
    </Page2ColumnLayout>
  );
};

export default Registration;
